/* eslint @typescript-eslint/strict-boolean-expressions: off */

import styles from "gui/chart/axis.module.css";
import { AxisBottom } from "@visx/axis";
import { AxisLeft } from "@visx/axis";
import { AxisOptions } from "lib/visuals/types";
import { AxisRight } from "@visx/axis";
import { ChartBounds } from "gui/chart/bounds";
import { ChartScale } from "gui/chart/scales";
import { ChartScales } from "gui/chart/scales";
import { ChartSeries } from "gui/chart/series";
import { GridRows } from "@visx/grid";
import { ScaleLinear } from "d3-scale";
import { ScaleTime } from "d3-scale";
import { TickRendererProps } from "@visx/axis";
import { VisualChart } from "lib/visuals/types";
import { formatNumber } from "lib/numbers/numbers";
import { formatTick } from "gui/chart/format";
import { isBandScale } from "gui/chart/scales";
import { leftAxisColumns } from "gui/chart/series";
import { rightAxisColumns } from "gui/chart/series";
import { useTheme } from "gui/theme/theme";

interface Props {
  visual: VisualChart;
  series: ChartSeries;
  bounds: ChartBounds;
  scales: ChartScales;
}

export const ChartAxis: React.FC<Props> = (props) => {
  const numTicksX = Math.floor(props.bounds.width / 100);
  const numTicksY = Math.floor(props.bounds.height / 100);
  const grid = props.scales.y0 || props.scales.y1;

  const theme = useTheme();

  return (
    <>
      {grid && (
        <GridRows
          scale={grid}
          width={props.bounds.xMax}
          height={props.bounds.yMax}
          className={styles.grid}
          numTicks={numTicksY}
          lineStyle={{ stroke: theme.grid_lines_color }}
        />
      )}
      {props.scales.y0 &&
        !isBandScale(props.scales.y0) &&
        !axisLogarithmic(props.visual.options.yAxis[0]) && (
          <AxisZeroLine {...props} scale={props.scales.y0} />
        )}
      {props.scales.y1 &&
        !isBandScale(props.scales.y1) &&
        !axisLogarithmic(props.visual.options.yAxis[1]) && (
          <AxisZeroLine {...props} scale={props.scales.y1} />
        )}
      {props.scales.x && (
        <AxisBottom
          axisLineClassName={styles.line}
          label={axisTitle(props.visual.options.xAxis)}
          labelProps={{
            className: styles.label,
            fill: theme.axis_legend_color,
            textAnchor: "middle",
            y: 33,
          }}
          numTicks={numTicksX}
          scale={props.scales.x}
          tickClassName={styles.tick}
          tickComponent={AxisTick}
          tickLength={5}
          tickStroke="transparent"
          tickValues={tickValuesX(props.scales.x, numTicksX)}
          top={props.bounds.yMax}
          tickFormat={
            props.visual.options.xAxis.tickFormat
              ? (value: unknown) =>
                  formatNumber(
                    value as number,
                    props.visual.options.xAxis.tickFormat
                  )
              : formatTick.bind(
                  null,
                  props.visual,
                  props.series,
                  props.series.x
                )
          }
        />
      )}
      {props.scales.y0 && (
        <AxisLeft
          axisLineClassName={styles.line}
          label={axisTitle(props.visual.options.yAxis[0])}
          labelProps={{
            className: styles.label,
            fill: theme.axis_legend_color,
            textAnchor: "middle",
          }}
          numTicks={numTicksY}
          scale={props.scales.y0}
          tickClassName={styles.tick}
          tickComponent={AxisTick}
          tickLength={5}
          tickStroke="transparent"
          tickFormat={
            props.visual.options.yAxis[0].tickFormat
              ? (value: unknown) =>
                  formatNumber(
                    value as number,
                    props.visual.options.yAxis[0].tickFormat
                  )
              : formatTick.bind(
                  null,
                  props.visual,
                  props.series,
                  leftAxisColumns(props.series)[0]
                )
          }
        />
      )}
      {props.scales.y1 && (
        <AxisRight
          axisLineClassName={styles.line}
          label={axisTitle(props.visual.options.yAxis[1])}
          labelProps={{
            className: styles.label,
            fill: theme.axis_legend_color,
            textAnchor: "middle",
          }}
          left={props.bounds.xMax}
          numTicks={numTicksY}
          scale={props.scales.y1}
          tickClassName={styles.tick}
          tickComponent={AxisTick}
          tickLength={5}
          tickStroke="transparent"
          tickFormat={
            props.visual.options.yAxis[1].tickFormat
              ? (value: unknown) =>
                  formatNumber(
                    value as number,
                    props.visual.options.yAxis[1].tickFormat
                  )
              : formatTick.bind(
                  null,
                  props.visual,
                  props.series,
                  rightAxisColumns(props.series)[0]
                )
          }
        />
      )}
    </>
  );
};

const AxisZeroLine: React.FC<
  Props & {
    scale: ScaleTime<number, number> | ScaleLinear<number, number>;
  }
> = (props) => {
  const theme = useTheme();
  const y0 = props.scale(0);

  // Avoid drawing zero-lines outside the main chart bounds.
  if (y0 < 0 || y0 > props.bounds.yMax) {
    return null;
  }

  return (
    <line
      x1={0}
      x2={props.bounds.xMax}
      y1={y0}
      y2={y0}
      className={styles.zero}
      stroke={theme.axis_zero_color}
    />
  );
};

const AxisTick: React.FC<TickRendererProps> = (props) => {
  const theme = useTheme();

  return (
    <svg
      x={props.dx}
      y={props.dy}
      fontSize={props.fontSize}
      style={{ overflow: "visible" }}
    >
      <text
        x={props.x}
        y={props.y}
        fill={theme.axis_text_color}
        fontFamily={props.fontFamily}
        fontSize={props.fontSize}
        textAnchor={props.textAnchor}
      >
        <tspan x={props.x} dy={0}>
          {props.formattedValue}
        </tspan>
      </text>
    </svg>
  );
};

// Hackily avoid overlapping x-axis ticks by limiting the
// number of ticks returned by d3's scale.ticks function.
const tickValuesX = (scale: ChartScale, ideal: number) => {
  if (isBandScale(scale)) {
    // Use AxisBottom.numTicks for column charts.
    return;
  }

  return (scale.ticks() as any[]).filter((_, i, ticks) => {
    return (
      // Keep all ticks if we are at or above the ideal number.
      ideal >= ticks.length ||
      // Always keep the first tick.
      i === 0 ||
      // Always keep the last tick.
      i === ticks.length - 1 ||
      // Keep some of the generated ticks.
      i % Math.floor(ticks.length / (ideal - 2)) === 0
    );
  });
};

const axisTitle = (axis?: AxisOptions) => {
  return axis?.title?.text;
};

const axisLogarithmic = (axis?: AxisOptions) => {
  return axis?.type === "logarithmic";
};
